import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import { FaExpand, FaTimes, FaChevronLeft, FaChevronRight, FaImages } from 'react-icons/fa';
import SectionHeader from '../SectionHeader/SectionHeader';

const gridVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.95 },
  visible: {
    opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } }
};

const iconVariants = {
  hidden: { scale: 0.6, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.22, ease: 'easeOut' } }
};

class Gallery extends Component {
  constructor() {
    super();
    this.state = {
      myData: [],
      selectedIndex: null,
      isModalOpen: false,
      loading: true,
      hoveredCard: null
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.photos).then(result => {
      this.setState({ myData: result || [], loading: false });
    }).catch(() => {
      this.setState({ loading: false });
    });
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.body.style.overflow = 'unset';
  }

  handleKeyDown = (e) => {
    if (!this.state.isModalOpen) return;
    if (e.key === 'ArrowRight') this.nextImage();
    else if (e.key === 'ArrowLeft') this.prevImage();
    else if (e.key === 'Escape') this.closeModal();
  };

  openModal = (index) => {
    this.setState({ selectedIndex: index, isModalOpen: true });
    document.body.style.overflow = 'hidden';
  };

  closeModal = () => {
    this.setState({ selectedIndex: null, isModalOpen: false });
    document.body.style.overflow = 'unset';
  };

  nextImage = () => {
    const { selectedIndex, myData } = this.state;
    const total = Math.min(myData.length, 12);
    this.setState({ selectedIndex: (selectedIndex + 1) % total });
  };

  prevImage = () => {
    const { selectedIndex, myData } = this.state;
    const total = Math.min(myData.length, 12);
    this.setState({ selectedIndex: (selectedIndex - 1 + total) % total });
  };

  render() {
    const { myData, isModalOpen, selectedIndex, loading, hoveredCard } = this.state;
    const displayData = myData.slice(0, 12);
    const selectedImage = selectedIndex !== null && displayData[selectedIndex]
      ? (displayData[selectedIndex].large_image || displayData[selectedIndex].multi_image)
      : null;

    if (loading) {
      return (
        <section className="py-12 sm:py-20 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header skeleton */}
            <div className="text-center mb-12">
              <div className="skel h-7 w-36 rounded-full mx-auto mb-4" />
              <div className="skel h-10 w-72 rounded-xl mx-auto mb-3" />
              <div className="skel h-4 w-80 rounded mx-auto mb-2" />
              <div className="skel h-4 w-60 rounded mx-auto" />
            </div>
            {/* Grid skeleton */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="skel rounded-xl sm:rounded-2xl" style={{ aspectRatio: '4 / 3' }} />
              ))}
            </div>
          </div>
        </section>
      );
    }

    return (
      <>
        {/* ── Gallery Section ── */}
        <section className="py-12 sm:py-20 bg-neutral-50">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">

            {/* Header */}
            <SectionHeader
              badge="Photo Gallery"
              title="Moments of Impact"
              subtitle="A glimpse into the lives we touch and the communities we serve around the world."
              variant="light"
              align="center"
              className="mb-12"
            />

            {/* Grid */}
            {displayData.length > 0 ? (
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4"
                variants={gridVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
              >
                {displayData.map((item, index) => {
                  const isHovered = hoveredCard === index;
                  return (
                    <motion.div
                      key={index}
                      className="relative aspect-[4/3] overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer shadow-md"
                      variants={itemVariants}
                      whileHover={{ y: -6, boxShadow: '0 20px 48px rgba(0,0,0,0.22)', transition: { duration: 0.28, ease: 'easeOut' } }}
                      onMouseEnter={() => this.setState({ hoveredCard: index })}
                      onMouseLeave={() => this.setState({ hoveredCard: null })}
                      onClick={() => this.openModal(index)}
                    >
                      <motion.img
                        src={item.small_image || item.multi_image}
                        alt={`Gallery image ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                        initial={{ scale: 1.04, opacity: 0 }}
                        animate={{
                          scale: isHovered ? 1.12 : 1,
                          opacity: 1
                        }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      />

                      {/* Overlay */}
                      <motion.div
                        className="absolute inset-0 flex items-center justify-center"
                        style={{ background: 'rgba(10,30,20,0.52)' }}
                        variants={overlayVariants}
                        animate={isHovered ? 'visible' : 'hidden'}
                        initial="hidden"
                      >
                        <motion.span
                          className="w-14 h-14 rounded-full flex items-center justify-center"
                          style={{ background: '#fff', boxShadow: '0 4px 24px rgba(0,0,0,0.35)' }}
                          variants={iconVariants}
                          animate={isHovered ? 'visible' : 'hidden'}
                          initial="hidden"
                        >
                          <FaExpand style={{ color: '#1a4731', fontSize: '18px' }} />
                        </motion.span>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : (
              <div className="text-center py-16">
                <FaImages className="text-6xl text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500">No gallery images available yet.</p>
              </div>
            )}

            {/* View All */}
            <motion.div
              className="text-center mt-10"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.25 }}
            >
              <motion.span
                style={{ display: 'inline-block' }}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.97 }}
              >
                <Link
                  to="/allgallery"
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-8 rounded-xl transition-colors duration-200 shadow-md"
                >
                  View All Gallery
                </Link>
              </motion.span>
            </motion.div>
          </div>
        </section>

        {/* ── Lightbox ── */}
        <AnimatePresence>
          {isModalOpen && selectedImage && (
            <motion.div
              className="fixed inset-0 z-50"
              style={{ backgroundColor: 'rgba(4,4,4,0.97)' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {/* Backdrop click closes */}
              <div className="absolute inset-0" onClick={this.closeModal} />

              {/* ── Top bar ── */}
              <div
                className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-5 sm:px-8"
                style={{
                  height: '60px',
                  background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, transparent 100%)'
                }}
              >
                <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', fontWeight: 500, letterSpacing: '0.03em' }}>
                  {selectedIndex + 1} &thinsp;/&thinsp; {displayData.length}
                </span>
                <button
                  onClick={this.closeModal}
                  style={{
                    width: '38px', height: '38px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.12)',
                    border: '1.5px solid rgba(255,255,255,0.22)',
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    backdropFilter: 'blur(6px)'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                >
                  <FaTimes style={{ fontSize: '15px' }} />
                </button>
              </div>

              {/* ── Main image — fills space between top bar and thumbnail strip ── */}
              <div
                className="absolute left-0 right-0 flex items-center justify-center"
                style={{ top: '60px', bottom: '100px' }}
              >
                <AnimatePresence mode="wait">
                  <motion.img
                    key={selectedIndex}
                    src={selectedImage}
                    alt={`Gallery image ${selectedIndex + 1}`}
                    style={{
                      maxHeight: '100%',
                      maxWidth: 'calc(100% - 110px)',
                      objectFit: 'contain',
                      borderRadius: '14px',
                      boxShadow: '0 32px 80px rgba(0,0,0,0.9)',
                      display: 'block',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                    initial={{ opacity: 0, scale: 0.93 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.93 }}
                    transition={{ duration: 0.2 }}
                  />
                </AnimatePresence>
              </div>

              {/* ── Prev button ── */}
              <button
                onClick={e => { e.stopPropagation(); this.prevImage(); }}
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 60,
                  width: '46px', height: '46px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.11)',
                  border: '1.5px solid rgba(255,255,255,0.22)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                  transition: 'background 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.11)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
              >
                <FaChevronLeft style={{ fontSize: '17px' }} />
              </button>

              {/* ── Next button ── */}
              <button
                onClick={e => { e.stopPropagation(); this.nextImage(); }}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 60,
                  width: '46px', height: '46px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.11)',
                  border: '1.5px solid rgba(255,255,255,0.22)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  backdropFilter: 'blur(8px)',
                  transition: 'background 0.2s, transform 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.11)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; }}
              >
                <FaChevronRight style={{ fontSize: '17px' }} />
              </button>

              {/* ── Thumbnail strip ── */}
              <div
                className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-start sm:justify-center gap-2 px-4 overflow-x-auto"
                style={{
                  height: '100px',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.82) 60%, transparent 100%)',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
                onClick={e => e.stopPropagation()}
              >
                {displayData.map((item, i) => (
                  <button
                    key={i}
                    onClick={() => this.setState({ selectedIndex: i })}
                    style={{
                      flexShrink: 0,
                      width: '54px',
                      height: '40px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: i === selectedIndex
                        ? '2.5px solid #ffffff'
                        : '2px solid rgba(255,255,255,0.15)',
                      opacity: i === selectedIndex ? 1 : 0.42,
                      transform: i === selectedIndex ? 'scale(1.18)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                      background: 'none',
                      padding: 0,
                    }}
                  >
                    <img
                      src={item.multi_image}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }
}

export default Gallery;
